import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { withTranslation, WithTranslation } from 'react-i18next';
import Icon from '@material-ui/core/Icon';
import Button from '@material-ui/core/Button';
import debounce from 'lodash/debounce';
// Components
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import EnhancedForm from '../../../components/form';
import { FormDatas, HeadCell } from '../../../local-types';
import { Dialog } from '../../../components/dialog';
import { TooltipButton } from '../../../components/tooltip-button';
import { AutoFill } from '../../../components/field-autofill';
import EnhancedTable from '../../../components/table';
// Modules
import { tableRequestPoints } from '../../../redux/reducers/table';
import { selectors as errorsSelectors, ErrorPayload } from '../../../redux/reducers/errors';
import {
    actions as prebookActions,
    selectors as prebookSelectors,
    types as prebookTypes,
} from '../../../redux/reducers/prebook';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations';
import { PrebookGroupCreationPayload, PrebookById } from '../../../redux/reducers/api-types';
// Ressources
import withGlobalStyles from '../../../utils/global-styles';
import { paths } from '../../paths';
import FormSubmit from '../../../components/form/formSubmit';
import { getListDriversURL } from '../../../utils/core-api';
import { clsx } from '../../../utils/htmlUtils';
import { AccountRight, RightRank } from '../../../utils/userUtils';

const queryString = require('query-string');

interface PrebooksAddPageProps extends WithTranslation, RouteComponentProps {
    globalClasses: any;
    prebooks: PrebookById;
    crudPrebookError: ErrorPayload;
    prebookDriversError: ErrorPayload;
    isSavingPrebook: boolean;
    getPrebook: (id: any) => any;
    updatePrebook: (id: any, payload: PrebookGroupCreationPayload) => any;
    createPrebook: (payload: PrebookGroupCreationPayload) => void;
    deletePrebook: (id: any) => void;
    addDriverToPrebook: (idDriver: number, idPrebook: any, callback: React.EffectCallback) => void;
    deleteDriverFromPrebook: (idDriver: number, callback: React.EffectCallback) => void;
}

interface AutoFillData {
    id: number;
    text: string;
    item: any;
}

const PrebooksAddPage = (props: PrebooksAddPageProps): JSX.Element => {
    const params = queryString.parse(props.location.search);
    const {
        t,
        globalClasses,
        prebooks,
        getPrebook,
        createPrebook,
        updatePrebook,
        deletePrebook,
        addDriverToPrebook,
        deleteDriverFromPrebook,
        crudPrebookError,
        isSavingPrebook,
    } = props;

    const tableRef = React.useRef(null);

    const [formData, setFormData] = React.useState<any>({});
    const [autofillData, setAutofillData] = React.useState<AutoFillData>({} as AutoFillData);

    useEffect(() => {
        if (params.id) {
            getPrebook(params.id);
        }
    }, [params.id, getPrebook]);

    useEffect(() => {
        const prebook = prebooks[params.id];
        if (prebook) {
            setFormData(prebook);
        }
    }, [prebooks, params.id]);

    const handleSubmit = (formData): void => {
        // console.log('Submitting', formData);
        const id = params.id;

        if (id) {
            updatePrebook(id, formData);
        } else {
            createPrebook(formData);
        }

        setFormData(formData);
    };

    const handleDelete = (): void => {
        const id = params.id;
        if (id) {
            deletePrebook(id);
        }
    };

    // Autocomplete
    const handleAddDriverSubmit = (event): void => {
        event.preventDefault();
        console.log(autofillData.id, params);
        if (autofillData.id) {
            addDriverToPrebook(autofillData.id, params.id, () => {
                if (tableRef?.current) {
                    tableRef.current.reload();
                }
            });
        }
    };

    const debounceFunction = debounce(function (item) {
        if (item.id) {
            deleteDriverFromPrebook(item.id, () => {
                if (tableRef?.current) {
                    tableRef.current.reload();
                }
            });
        }
    }, 500);

    const handleDeleteDriverAction = (item: any) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        debounceFunction(item);
    };

    const generateInputs = (): FormDatas[] => {
        const inputs: FormDatas[] = [
            {
                title: t('common:form.general_infos'),
                datas: [
                    { id: 'name', label: t('common:form.lastname'), required: true },
                    {
                        id: 'min_prebook_interval',
                        label: t('form.min_prebook_interval'),
                        type: 'number',
                        inputsAttributs: { step: 1 },
                        numberNegative: false,
                        required: true,
                    },
                    {
                        id: 'suspension_duration',
                        label: t('form.suspension_duration'),
                        type: 'number',
                        inputsAttributs: { step: 1 },
                        numberNegative: false,
                        required: true,
                    },
                ],
            },
        ];
        if (params.id) {
            inputs.push({
                title: t('form.drivers'),
                datas: [
                    {
                        id: 'drivers',
                        label: t('drivers'),
                        type: 'create',
                        customView: (val, handleChange) => {
                            return (
                                <>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexWrap: 'wrap',
                                            flex: 1,
                                        }}
                                    >
                                        <AutoFill
                                            formControl={false}
                                            datas={{
                                                id: 'driver',
                                                label: t('form.driver_add'),
                                                minChar: 1,
                                                required: true,
                                                style: { minWidth: 200, width: '100%', flex: 1, marginRight: 8 },
                                                variant: 'outlined',
                                                // filter: {
                                                //     existingList: companyToPricingSetsList?.map((x) => x.company_id),
                                                //     test: (list, element) => !list.includes(element.uuid),
                                                // },
                                                onChange: (id, text, item) => {
                                                    setAutofillData({ id, text, item });
                                                },
                                                inputParams: { form: 'form_add_driver' },
                                                loaderPosition: 'right',
                                                api: getListDriversURL(),
                                            }}
                                        />
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            form="form_add_driver"
                                            style={{ marginLeft: 16, alignSelf: 'normal' }}
                                            color={autofillData.id !== -1 ? 'inherit' : 'secondary'}
                                        >
                                            <Icon fontSize="large">add</Icon>
                                        </Button>
                                    </div>
                                    <EnhancedTable
                                        onRef={(ref) => (tableRef.current = ref)}
                                        headers={headerDriverBuilder()}
                                        type={tableRequestPoints.DRIVERS}
                                        filterParams={{ 'filters[details.prebookGroup.id]': params.id }}
                                        filterVisible={false}
                                    />
                                </>
                            );
                        },
                    },
                ],
            });
        }
        return inputs;
    };

    const headerDriverBuilder = (): HeadCell[] => {
        const headers: HeadCell[] = [
            { id: 'fullname', label: t('common:form.lastname') },
            {
                id: 'action',
                label: t('common:action'),
                filterable: false,
                formatCell: (row) => {
                    return (
                        <TooltipButton title={t('common:delete')}>
                            <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={handleDeleteDriverAction(row)}
                                className={clsx(globalClasses.btnAutoWidth, globalClasses.btnDanger)}
                            >
                                <Icon fontSize="small">delete</Icon>
                            </Button>
                        </TooltipButton>
                        // <Dialog
                        //     btnTitle={t('common:validate')}
                        //     description={t('common:deleteParam', { id: row.id })}
                        //     customButton={
                        //         <TooltipButton title={t('common:delete')}>
                        //             <Button
                        //                 size="small"
                        //                 variant="contained"
                        //                 color="primary"
                        //                 className={clsx(globalClasses.btnAutoWidth, globalClasses.btnDanger)}
                        //             >
                        //                 <Icon fontSize="small">delete</Icon>
                        //             </Button>
                        //         </TooltipButton>
                        //     }
                        //     onOK={handleDeleteDriverAction(row)}
                        // />
                    );
                },
            },
        ];
        return headers;
    };

    const renderRemovePrebook = (): JSX.Element => {
        const id = params.id;
        const prebook = prebooks[id];

        if (!id || !prebook) {
            return null;
        }

        return (
            <Dialog
                btnTitle={t('common:validate')}
                description={t('common:deleteParam', {
                    id: `(${id}) ${prebook?.name ?? ''}`,
                })}
                customButton={
                    <TooltipButton title={t('common:deleteForever')}>
                        <Button
                            startIcon={<Icon>delete_forever</Icon>}
                            variant="contained"
                            className={clsx(globalClasses.roundButton, globalClasses.btnDanger)}
                            color="inherit"
                        >
                            {t('common:delete')}
                        </Button>
                    </TooltipButton>
                }
                onOK={handleDelete}
            />
        );
    };

    return (
        <App needRights={{ name: AccountRight.DRIVER_PREBOOK, mode: RightRank.W }}>
            <ErrorMessage payload={crudPrebookError} />
            <ErrorMessage payload={props.prebookDriversError} />
            {(!params.id || (formData && formData.id)) && (
                <>
                    <FormSubmit debounceSubmit={{ callback: handleAddDriverSubmit }} id="form_add_driver">
                        <input type="submit" hidden value="Ok" />
                    </FormSubmit>
                    <EnhancedForm
                        payload={formData}
                        inputs={generateInputs()}
                        backRoute={paths.prebooks}
                        onRemove={renderRemovePrebook()}
                        onSubmit={handleSubmit}
                        loader={isSavingPrebook}
                    />
                </>
            )}
        </App>
    );
};

const PrebooksAddPageWithStyleAndTranslation = withGlobalStyles(
    withTranslation(['prebook', 'common'])(PrebooksAddPage),
);

const mapStateToProps = (state) => ({
    prebooks: prebookSelectors.prebooks(state),
    crudPrebookError: errorsSelectors.crudPrebookError(state),
    prebookDriversError: errorsSelectors.prebookDriversError(state),
    isSavingPrebook: pendingSelectors.isPending(state, prebookTypes.CREATE_PREBOOK, prebookTypes.UPDATE_PREBOOK),
});

const mapDispatchToProps = {
    getPrebook: prebookActions.getPrebook,
    createPrebook: prebookActions.createPrebook,
    updatePrebook: prebookActions.updatePrebook,
    deletePrebook: prebookActions.deletePrebook,
    addDriverToPrebook: prebookActions.addDriverToPrebook,
    deleteDriverFromPrebook: prebookActions.deleteDriverFromPrebook,
};

export default connect(mapStateToProps, mapDispatchToProps)(PrebooksAddPageWithStyleAndTranslation);
